import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { Button, Table, Modal, Form, Input, Select, DatePicker, message, Space, Card, Typography, Row, Col, Statistic, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, FileExcelOutlined, FileAddOutlined, MailOutlined, SearchOutlined } from '@ant-design/icons';
import type { TableProps, TablePaginationConfig } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Key } from 'antd/es/table/interface';
import type { FilterValue } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import { handleApiError } from '../utils/errorHandler';

const { Title } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  companyName?: string;
  createdAt: string;
  updatedAt: string;
}

interface Company {
  id: string;
  name: string;
  type: string;
}

interface Metrics {
  totalContacts: number;
  individualContacts: number;
  companyContacts: number;
}

const ContactManagement: React.FC = (): JSX.Element => {
  console.log('ContactManagement component mounted');
  
  const navigate = useNavigate();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [pagination, setPagination] = useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `Total ${total} items`,
  });
  const [metrics, setMetrics] = useState<Metrics>({
    totalContacts: 0,
    individualContacts: 0,
    companyContacts: 0
  });

  const fetchContacts = useCallback(async () => {
    console.log('fetchContacts called with params:', {
      page: pagination.current,
      limit: pagination.pageSize,
      search: searchText,
      dateRange
    });
    
    setLoading(true);
    try {
      console.log('Making API request to /contacts');
      const response = await api.get<{ data: Contact[]; pagination: any }>('/contacts', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchText,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD')
        }
      });
      console.log('API response received:', response.data);
      
      if (response.data.data) {
        setContacts(response.data.data);
        calculateMetrics(response.data.data);
        // Update pagination from response
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            current: response.data.pagination.page,
            pageSize: response.data.pagination.limit,
            total: response.data.pagination.total
          }));
        }
      } else {
        message.error('Failed to fetch contacts');
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
      message.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, [pagination.current, pagination.pageSize, searchText, dateRange]);

  const calculateMetrics = (data: Contact[]) => {
    setMetrics({
      totalContacts: data.length,
      individualContacts: data.filter(contact => !contact.company).length,
      companyContacts: data.filter(contact => contact.company).length
    });
  };

  useEffect(() => {
    console.log('useEffect triggered with fetchContacts dependency');
    fetchContacts();
  }, [fetchContacts]);

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setIsModalVisible(true);
    form.setFieldsValue(contact);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/contacts/${id}`);
      message.success('Contact deleted successfully');
      fetchContacts();
    } catch (error) {
      console.error('Error deleting contact:', error);
      message.error('Failed to delete contact');
    }
  };

  const handleCreate = () => {
    setEditingContact(null);
    setIsModalVisible(true);
    form.resetFields();
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      if (editingContact) {
        await api.put(`/contacts/${editingContact.id}`, values);
        message.success('Contact updated successfully');
      } else {
        await api.post('/contacts', values);
        message.success('Contact created successfully');
      }
      setIsModalVisible(false);
      fetchContacts();
    } catch (error) {
      console.error('Error saving contact:', error);
      message.error('Failed to save contact');
    }
  };

  const columns: ColumnsType<Contact> = [
    {
      title: 'Name',
      key: 'name',
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/contacts/${record.id}`)}
          style={{ padding: 0, height: 'auto' }}
        >
          {record.name}
        </Button>
      ),
      sorter: true,
    },
    {
      title: 'Company',
      key: 'company',
      render: (_, record) => (
        record.companyName ? (
          <Tag color="blue">{record.companyName}</Tag>
        ) : (
          <Tag color="green">Individual</Tag>
        )
      ),
      sorter: true,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      render: (title: string) => title || 'N/A',
      sorter: true,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (email: string) => email || 'N/A',
      sorter: true,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (phone: string) => phone || 'N/A',
      sorter: true,
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      sorter: true,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          />
          <Button
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
            size="small"
            danger
          />
        </Space>
      ),
    },
  ];

  const handleTableChange: TableProps<Contact>['onChange'] = (pagination, filters, sorter) => {
    if (pagination) {
      setPagination(pagination);
    }
  };

  console.log('ContactManagement rendering with state:', {
    contacts: contacts.length,
    loading,
    error,
    searchText,
    pagination: pagination.current
  });

  return (
    <div className="p-6">
      <Row gutter={[16, 16]}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Contacts"
              value={metrics.totalContacts}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Individual Contacts"
              value={metrics.individualContacts}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Company Contacts"
              value={metrics.companyContacts}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card className="mt-6">
        <div className="flex justify-between items-center mb-4">
          <Title level={4}>Contacts</Title>
          <Space>
            <Input
              placeholder="Search contacts..."
              prefix={<SearchOutlined />}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 200 }}
            />
            <RangePicker
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  setDateRange([dates[0], dates[1]]);
                } else {
                  setDateRange(null);
                }
              }}
              allowClear
            />
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            >
              Add Contact
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={contacts}
          rowKey="id"
          pagination={pagination}
          loading={loading}
          onChange={handleTableChange}
          rowSelection={{
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) => {
              setSelectedRowKeys(newSelectedRowKeys);
            },
          }}
        />
      </Card>

      <Modal
        title={editingContact ? 'Edit Contact' : 'New Contact'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="firstName"
                label="First Name"
                rules={[{ required: true, message: 'Please enter first name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="lastName"
                label="Last Name"
                rules={[{ required: true, message: 'Please enter last name' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title"
                label="Title"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="department"
                label="Department"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Phone"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Address"
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item
            name="companyId"
            label="Company (Optional)"
          >
            <Select
              placeholder="Select a company"
              allowClear
            >
              {/* This would need to be populated with companies from the API */}
              <Option value="">No Company</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ContactManagement; 